<script setup lang="ts">

import { ref, computed, onMounted, onBeforeMount, onBeforeUnmount } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useStore } from "vuex"
import { v4 as uuidv4 } from "uuid"
import firebase from "@/firebase"
import "firebase/storage"
import { updateData, useGetDocs } from "@/mixins/modularFirebaseOperations"
import { formatUser, formatDate, modalClosed } from "@/mixins/visualFormatters"
import {
    getQuantityLabel,
    formatMoney,
    conditionLabels,
    quantityTypeOptions,
    createBatch,
    sortBatchesFIFO,
    purchaseCostByCurrency,
    getWorksiteLabel,
    requiresWholeUnits,
    addBatchTransaction,
    removeStockTransaction,
} from "@/composables/inventoryHelpers"
import resetBackground from "@/mixins/resetBackground"
import removeBackground from "@/mixins/removeBackground"
import AcMediumModal from "@/components/reusable/teleports/acMediumModal.vue"
import AccaDnDUpload from "@/components/reusable/accaDnDUpload.vue"
import InventoryBatchForm from "@/components/reusable/form-widgets/inventoryBatchForm.vue"
import EditableField from "@/components/reusable/editableField.vue"
import EditableTextarea from "@/components/reusable/editableTextarea.vue"
import CommentField from "@/components/reusable/commentField.vue"
import CommentListDisplayer from "@/components/reusable/commentListDisplayer.vue"
import type { AcInventoryItem, AcInventoryUpdate, AcProductBrand, AcSupplier, AcProductCategory, AcInventoryLocation, AcInventoryWorksite } from "@/types/inventory"

// Translated labels for the stock-movement update types
const updateTypeLabels: Record<AcInventoryUpdate["updateType"], string> = {
    ADD: "Ekleme",
    REMOVE: "Çıkartma",
    UPDATE: "Güncelleme",
}

const COLLECTION = "inventory/items/list"

const route = useRoute()
const router = useRouter()
const store = useStore()
const itemId = route.params.id as string

const userProfile = computed(() => store.getters["user/userProfile"])
const formattedUser = computed(() => formatUser(userProfile.value))

const item = ref<(AcInventoryItem & { id: string }) | null>(null)
const isLoading = ref(true)
const isUploadingImage = ref(false)

let unsubscribe: (() => void) | null = null

// Meta lists for the editable dropdowns
const { docs: brands } = useGetDocs<AcProductBrand>("inventory/brands/list")
const { docs: suppliers } = useGetDocs<AcSupplier>("inventory/suppliers/list")
const { docs: categories } = useGetDocs<AcProductCategory>("inventory/categories/list")
const { docs: locations } = useGetDocs<AcInventoryLocation>("inventory/locations/list")
const { docs: worksites } = useGetDocs<AcInventoryWorksite>("inventory/worksites/list")

const brandOptions = computed(() => brands.value.map((b) => ({ label: b.name, value: b })))
const supplierOptions = computed(() => suppliers.value.map((s) => ({ label: s.name, value: s })))
const categoryOptions = computed(() => categories.value.map((c) => ({ label: c.name, value: c })))
const locationOptions = computed(() => locations.value.map((l) => ({ label: l.name, value: l })))
// Only active worksites are offered as removal destinations.
const activeWorksites = computed(() => worksites.value.filter((w) => w.isActive))
const quantityTypeSelectOptions = quantityTypeOptions.map((o) => ({ label: o.label, value: o.value }))

// ---- Derived values ----
const sortedBatches = computed(() => sortBatchesFIFO(item.value?.batches ?? []))

const totalValue = computed(() => {
    if (!item.value?.quantity || !item.value?.currentValue) return null
    return item.value.quantity * item.value.currentValue
})

const purchaseCosts = computed(() => purchaseCostByCurrency(item.value?.batches))

const toDisplayDate = (value: any): string => {
    if (!value) return "-"
    const date = value?.toDate ? value.toDate() : new Date(value)
    return formatDate(date)
}

const supplierName = (supplier: AcInventoryItem["supplier"]): string => {
    if (!supplier) return "-"
    return typeof supplier === "string" ? supplier : supplier.name || "-"
}

// Stock changes are persisted via Firestore transactions (see inventoryHelpers); the
// live onSnapshot listener below refreshes `item` once the write lands.

// ---- Add stock (new batch) ----
const isAddingBatch = ref(false)
const handleBatchSubmitted = async (payload: any) => {
    if (!item.value) return
    const batch = createBatch({ ...payload, addedBy: formattedUser.value })
    try {
        await addBatchTransaction(itemId, batch, formattedUser.value, item.value.quantityType)
    } catch (error: any) {
        alert(error?.message ?? `Bir hata oluştu: ${error}`)
        return
    }
    isAddingBatch.value = false
    modalClosed()
}

// ---- Remove stock (FIFO) ----
const isRemoving = ref(false)
const removeAmount = ref<number | null>(null)
const removeWorksiteId = ref<string>("")
const confirmRemove = async () => {
    if (!item.value) return
    const amount = Number(removeAmount.value)
    const label = getQuantityLabel(item.value.quantityType)
    if (isNaN(amount) || amount <= 0) {
        alert("Lütfen geçerli bir miktar giriniz.")
        return
    }
    if (requiresWholeUnits(item.value.quantityType) && !Number.isInteger(amount)) {
        alert("Bu ürün adet ile takip edilir; lütfen tam sayı giriniz.")
        return
    }
    if (amount > item.value.quantity) {
        alert(`Mevcut stoktan (${item.value.quantity} ${label}) daha fazlasını çıkaramazsınız.`)
        return
    }
    const worksite = activeWorksites.value.find((w) => w.id === removeWorksiteId.value) ?? null
    try {
        // InsufficientStockError carries a localized message; any other error falls through too.
        await removeStockTransaction(itemId, amount, formattedUser.value, item.value.quantityType, worksite)
    } catch (error: any) {
        alert(error?.message ?? `Bir hata oluştu: ${error}`)
        return
    }
    isRemoving.value = false
    removeAmount.value = null
    removeWorksiteId.value = ""
    modalClosed()
}

// ---- Inline editable item-level fields ----
const updateSingleItemField = async (field: string, value: any) => {
    await updateData(COLLECTION, itemId, {
        [field]: value,
        updatedAt: new Date(),
        updatedBy: formattedUser.value,
    })
}

// ---- Comments ----
const addCommentToItem = async (comment: string) => {
    const commentObj = {
        comment,
        date: new Date(),
        user: formattedUser.value,
    }
    await updateData(COLLECTION, itemId, {
        comments: firebase.firestore.FieldValue.arrayUnion(commentObj),
        updatedAt: new Date(),
        updatedBy: formattedUser.value,
    })
}

// ---- Delete (archive) ----
const handleDelete = async () => {
    const confirmation = confirm("Bu ürünü arşivlemek istediğinize emin misiniz? Ürün stok listesinde görünmeyecektir.")
    if (!confirmation) return
    await updateData(COLLECTION, itemId, {
        status: "ARCHIVED",
        updatedAt: new Date(),
        updatedBy: formattedUser.value,
    })
    router.push("/inventory/items")
}

// ---- Image upload ----
const uploadInventoryImage = async (file: File): Promise<string | undefined> => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const ext = file.name.split(".").pop()
    const fileName = `${uuidv4()}.${ext}`
    const fileRef = firebase.storage().ref().child(`inventory/${year}/${month}/${fileName}`)
    try {
        await fileRef.put(file)
        return await fileRef.getDownloadURL()
    } catch (error) {
        console.error(error)
        alert(`Görsel yüklenirken bir hata oluştu: ${error}`)
        return undefined
    }
}

const handleImageSelected = async (files: File[]) => {
    if (!files || !files.length) return
    isUploadingImage.value = true
    const url = await uploadInventoryImage(files[0])
    if (url) await updateSingleItemField("productImage", url)
    isUploadingImage.value = false
}

onMounted(() => {
    unsubscribe = firebase.firestore().doc(`${COLLECTION}/${itemId}`).onSnapshot((doc) => {
        item.value = doc.exists ? ({ id: doc.id, ...doc.data() } as AcInventoryItem & { id: string }) : null
        isLoading.value = false
    }, (error) => {
        console.error(error)
        isLoading.value = false
    })
})

onBeforeMount(() => {
    removeBackground.methods.removeBackground()
})

onBeforeUnmount(() => {
    if (unsubscribe) unsubscribe()
    resetBackground.methods.resetBackground()
})

</script>

<template>
    <div class="main-container content-start">
        <div class="flex col-start-1 col-end-13 justify-between items-center w-full mt-8">
            <h2 class="ac-large-title mb-0">{{ item?.productName || "Ürün Detayı" }}</h2>
            <button type="button" class="ac-add-button" @click="router.push('/inventory/items')">
                ← Stok Listesine Dön
            </button>
        </div>

        <p v-if="isLoading" class="col-start-1 col-end-13 mt-6 text-gray-500">Yükleniyor...</p>
        <p v-else-if="!item" class="col-start-1 col-end-13 mt-6 text-gray-500">Ürün bulunamadı.</p>

        <section v-else class="two-column-container sidebar-right col-start-1 col-end-13 mt-10">
            <main>
                <!-- ÜRÜN BİLGİLERİ -->
                <h2 class="subtitle mb-6">Ürün Bilgileri</h2>
                <div class="detail-card">
                    <div class="detail-grid">
                        <div class="detail-field">
                            <span class="detail-label">Ürün Adı</span>
                            <editable-field :value="item.productName" type="text" @update="updateSingleItemField('productName', $event)" />
                        </div>
                        <div class="detail-field">
                            <span class="detail-label">Marka</span>
                            <editable-field :value="item.productBrand" type="select" :options="brandOptions" :display="item.productBrand?.name" placeholder="Marka seçiniz" @update="updateSingleItemField('productBrand', $event)" />
                        </div>
                        <div class="detail-field">
                            <span class="detail-label">Tedarikçi</span>
                            <editable-field :value="item.supplier" type="select" :options="supplierOptions" :display="supplierName(item.supplier)" placeholder="Tedarikçi seçiniz" @update="updateSingleItemField('supplier', $event)" />
                        </div>
                        <div class="detail-field">
                            <span class="detail-label">Kategori</span>
                            <editable-field :value="item.productCategory" type="select" :options="categoryOptions" :display="item.productCategory?.name" placeholder="Kategori seçiniz" @update="updateSingleItemField('productCategory', $event)" />
                        </div>
                        <div class="detail-field">
                            <span class="detail-label">SKU / Stok Kodu</span>
                            <editable-field :value="item.productSKU" type="text" placeholder="SKU giriniz" @update="updateSingleItemField('productSKU', $event)" />
                        </div>
                    </div>
                    <div class="detail-field mt-6">
                        <span class="detail-label">Açıklama</span>
                        <editable-textarea
                            :text="item.productDescription"
                            placeholder="buraya tıklayarak bir ürün açıklaması girebilirsiniz"
                            @edit-completed="updateSingleItemField('productDescription', $event)"
                        />
                    </div>
                </div>

                <!-- ADET -->
                <h2 class="subtitle !mt-[3.6rem] mb-6">Adet</h2>
                <div class="detail-card">
                    <div class="detail-grid">
                        <div class="detail-field">
                            <span class="detail-label">Toplam Miktar</span>
                            <span v-if="item.quantity === 0" class="font-semibold text-red-600">Stokta yok</span>
                            <span v-else>{{ item.quantity }} {{ getQuantityLabel(item.quantityType) }}</span>
                        </div>
                        <div class="detail-field">
                            <span class="detail-label">Miktar Birimi</span>
                            <editable-field :value="item.quantityType" type="select" :options="quantityTypeSelectOptions" @update="updateSingleItemField('quantityType', $event)" />
                        </div>
                        <div class="detail-field">
                            <span class="detail-label">Yenileme Eşiği</span>
                            <editable-field :value="item.renewThreshold ?? 0" type="number" @update="updateSingleItemField('renewThreshold', $event)" />
                        </div>
                    </div>
                    <hr class="my-6" />
                    <div class="detail-grid">
                        <div class="detail-field"><span class="detail-label">İlk Alış Birim Fiyatı</span><span>{{ formatMoney(item.originalPrice, item.purchaseCurrency) }}</span></div>
                        <div class="detail-field">
                            <span class="detail-label">Güncel Birim Değer</span>
                            <editable-field :value="item.currentValue ?? 0" type="number" :label="item.purchaseCurrency" @update="updateSingleItemField('currentValue', $event)" />
                        </div>
                        <div class="detail-field"><span class="detail-label">Toplam Güncel Değer</span><span class="font-semibold">{{ formatMoney(totalValue, item.purchaseCurrency) }}</span></div>
                        <div class="detail-field">
                            <span class="detail-label">Toplam Alış Maliyeti</span>
                            <span v-if="purchaseCosts.length">
                                <span v-for="(cost, i) in purchaseCosts" :key="cost.currency">
                                    {{ i > 0 ? " + " : "" }}{{ formatMoney(cost.total, cost.currency) }}
                                </span>
                            </span>
                            <span v-else>-</span>
                        </div>
                    </div>
                </div>

                <!-- PARTİLER / SEGMENTASYON -->
                <div class="flex items-center justify-between !mt-[3.6rem] mb-6">
                    <h2 class="subtitle mb-0">Partiler (Satın Alma Geçmişi)</h2>
                    <button class="ac-add-button text-2xl" @click="isAddingBatch = true">Parti Ekle</button>
                </div>
                <table v-if="sortedBatches.length" class="alt-table w-full">
                    <thead>
                        <tr>
                            <th>Alış Tarihi</th>
                            <th>Durum</th>
                            <th>Kalan / Alınan</th>
                            <th>Birim Fiyat</th>
                            <th>Kalan Tutar</th>
                            <th>Not</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="batch in sortedBatches" :key="batch.id" :class="{ 'opacity-50': batch.quantity === 0 }">
                            <td>{{ toDisplayDate(batch.purchaseDate) }}</td>
                            <td>{{ conditionLabels[batch.condition] }}</td>
                            <td>{{ batch.quantity }} / {{ batch.initialQuantity }} {{ getQuantityLabel(item.quantityType) }}</td>
                            <td>{{ formatMoney(batch.unitPrice, batch.purchaseCurrency) }}</td>
                            <td>{{ formatMoney(batch.quantity * batch.unitPrice, batch.purchaseCurrency) }}</td>
                            <td>{{ batch.note || "-" }}</td>
                        </tr>
                    </tbody>
                </table>
                <p v-else class="text-2xl italic text-gray-400">Henüz parti kaydı bulunmuyor.</p>

                <!-- Stok Hareketleri -->
                <h2 class="subtitle !mt-[3.6rem] mb-6">Stok Hareketleri</h2>
                <table v-if="item.updates && item.updates.length" class="alt-table w-full">
                    <thead>
                        <tr>
                            <th>Tarih</th>
                            <th>İşlem</th>
                            <th>Açıklama</th>
                            <th>Hedef Şantiye</th>
                            <th>Kullanıcı</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(update, index) in [...item.updates].reverse()" :key="index">
                            <td>{{ toDisplayDate(update.updateDate) }}</td>
                            <td>{{ updateTypeLabels[update.updateType] || update.updateType }}</td>
                            <td>{{ update.updateDescription }}</td>
                            <td>{{ update.updateType === "REMOVE" ? getWorksiteLabel(update.worksite) : "-" }}</td>
                            <td>{{ update.updateBy?.userName || "-" }}</td>
                        </tr>
                    </tbody>
                </table>
                <p v-else class="text-2xl italic text-gray-400">Henüz stok hareketi bulunmuyor.</p>

                <!-- Yorumlar -->
                <h2 class="subtitle !mt-[3.6rem] mb-6">Yorumlar</h2>
                <div v-if="item.comments && item.comments.length" class="mb-8">
                    <comment-list-displayer :comments="item.comments" />
                </div>
                <p v-else class="text-2xl italic text-gray-400 mb-6">Bu ürün için henüz yorum eklenmemiş.</p>
                <comment-field
                    class="w-3/4 pb-12"
                    placeholder="Yorumunuzu buraya yazın veya iptal etmek için 'esc' tuşuna basın."
                    @comment-submitted="addCommentToItem($event)"
                />
            </main>

            <aside>
                <!-- Product image -->
                <div class="image-area mb-8">
                    <img v-if="item.productImage" :src="item.productImage" alt="Ürün Görseli" class="product-image" />
                    <div v-else>
                        <p v-if="isUploadingImage" class="text-center text-gray-500 py-8">Görsel yükleniyor...</p>
                        <AccaDnDUpload
                            v-else
                            :single-file="true"
                            :allow="['image/*']"
                            @files-changed="handleImageSelected"
                        />
                    </div>
                </div>

                <!-- Actions -->
                <h2 class="subtitle mb-6">İşlemler</h2>
                <div class="flex flex-col">
                    <button class="ac-primary-button w-full text-2xl mb-4" @click="isAddingBatch = true">Stok Ekle (Parti)</button>
                    <button class="ac-secondary-button w-full text-2xl mb-4" @click="isRemoving = true">Stok Çıkar</button>
                    <button class="ac-secondary-button button-negative w-full text-2xl mb-4" @click="handleDelete">Sil (Arşivle)</button>
                </div>

                <!-- Location information -->
                <div class="info-box mt-[3.6rem]">
                    <h3 class="mb-0">Konum Bilgileri</h3>
                    <div class="detail-field">
                        <span class="detail-label">Depo</span>
                        <editable-field :value="item.location" type="select" :options="locationOptions" :display="item.location?.name" placeholder="Depo seçiniz" @update="updateSingleItemField('location', $event)" />
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Alt Konum</span>
                        <editable-field :value="item.subLocation" type="text" placeholder="Alt konum giriniz" @update="updateSingleItemField('subLocation', $event)" />
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Alt Konum Detayı</span>
                        <editable-field :value="item.subLocationDetail" type="text" placeholder="Detay giriniz" @update="updateSingleItemField('subLocationDetail', $event)" />
                    </div>
                </div>

                <!-- Warranty information -->
                <div class="info-box mt-[3.6rem]">
                    <h3 class="mb-0">Garanti Bilgileri</h3>
                    <div class="detail-field">
                        <span class="detail-label">Garanti Süresi (ay)</span>
                        <editable-field :value="item.warrantyPeriod ?? 0" type="number" @update="updateSingleItemField('warrantyPeriod', $event)" />
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Garanti Başlangıcı</span>
                        <editable-field :value="item.warrantyStart" type="date" placeholder="Tarih seçiniz" @update="updateSingleItemField('warrantyStart', $event)" />
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Garanti Bitişi</span>
                        <editable-field :value="item.warrantyEnd" type="date" placeholder="Tarih seçiniz" @update="updateSingleItemField('warrantyEnd', $event)" />
                    </div>
                </div>

                <!-- Meta information -->
                <div class="info-box mt-[3.6rem]">
                    <h3 class="mb-0">Kayıt Bilgileri</h3>
                    <div class="detail-field" v-if="item.addedBy">
                        <span class="detail-label">Ekleyen</span>
                        <span class="flex items-center gap-2">
                            <img v-if="item.addedBy.profileImage" :src="item.addedBy.profileImage" referrerpolicy="no-referrer" alt="" class="rounded-image-mini rounded-full" />
                            {{ item.addedBy.userName || "-" }}
                        </span>
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Oluşturulma</span>
                        <span class="flex items-center gap-2">
                            <img class="w-6 filter-main" src="../../assets/icons/calendar.png" alt="">
                            {{ toDisplayDate(item.createdAt) }}
                        </span>
                    </div>
                    <div class="detail-field" v-if="item.updatedBy">
                        <span class="detail-label">Güncelleyen</span>
                        <span class="flex items-center gap-2">
                            <img v-if="item.updatedBy.profileImage" :src="item.updatedBy.profileImage" referrerpolicy="no-referrer" alt="" class="rounded-image-mini rounded-full" />
                            {{ item.updatedBy.userName || "-" }}
                        </span>
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Son Güncelleme</span>
                        <span class="flex items-center gap-2">
                            <img class="w-6 filter-main" src="../../assets/icons/calendar.png" alt="">
                            {{ toDisplayDate(item.updatedAt) }}
                        </span>
                    </div>
                </div>
            </aside>
        </section>

        <!-- Add batch modal -->
        <teleport to="body">
            <transition
                appear
                type="animation"
                enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
            >
                <ac-medium-modal
                    v-if="isAddingBatch && item"
                    class="lg:!top-44 overflow-y-scroll"
                    width="44vw"
                    height="fit-content"
                    title="Stok Ekle (Yeni Parti)"
                    @modal-closed="isAddingBatch = false; modalClosed()"
                >
                    <inventory-batch-form
                        :quantity-label="getQuantityLabel(item.quantityType)"
                        :default-currency="item.purchaseCurrency"
                        :integer-only="requiresWholeUnits(item.quantityType)"
                        @batch-submitted="handleBatchSubmitted"
                        @cancel="isAddingBatch = false; modalClosed()"
                    />
                </ac-medium-modal>
            </transition>
        </teleport>

        <!-- Remove (FIFO) modal -->
        <teleport to="body">
            <transition
                appear
                type="animation"
                enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
            >
                <ac-medium-modal
                    v-if="isRemoving && item"
                    class="lg:!top-44"
                    width="34vw"
                    height="fit-content"
                    title="Stok Çıkar"
                    @modal-closed="isRemoving = false; removeAmount = null; removeWorksiteId = ''; modalClosed()"
                >
                    <div class="ac-main-form !border-none">
                        <p class="mb-4 text-xl">
                            <strong>{{ item.productName }}</strong> —
                            mevcut miktar: {{ item.quantity }} {{ getQuantityLabel(item.quantityType) }}
                        </p>
                        <p class="mb-4 text-lg text-gray-500">En eski partiden başlayarak (FIFO) düşülecektir.</p>
                        <div class="ac-form-control w-full">
                            <label for="removeAmount">Çıkarılacak miktar</label>
                            <input id="removeAmount" type="number" min="1" :max="item.quantity" v-model="removeAmount" @keyup.enter="confirmRemove">
                        </div>
                        <div class="ac-form-control w-full">
                            <label for="removeWorksite">Gönderilen Şantiye</label>
                            <select id="removeWorksite" v-model="removeWorksiteId">
                                <option value="">Şantiye Belirtilmemiş</option>
                                <option v-for="worksite in activeWorksites" :key="worksite.id" :value="worksite.id">
                                    {{ worksite.name }}
                                </option>
                            </select>
                        </div>
                        <div class="flex items-center justify-center gap-6 w-full">
                            <button type="button" class="ac-primary-button text-2xl mt-4" @click="confirmRemove">Kaydet</button>
                            <button type="button" class="ac-secondary-button text-2xl mt-4" @click="isRemoving = false; removeAmount = null; removeWorksiteId = ''; modalClosed()">İptal</button>
                        </div>
                    </div>
                </ac-medium-modal>
            </transition>
        </teleport>
    </div>
</template>

<style lang="scss">

@import "../../styles/global/helpers.scss";
@import "../../styles/core/variables.scss";
@import "../../styles/core/typography.scss";
@import "../../styles/global/background.scss";
@import "../../styles/global/buttons.scss";
@import "../../styles/global/tables.scss";

</style>

<style scoped>
.detail-card {
    background-color: white;
    border-radius: 0.75rem;
    box-shadow: var(--main-box-shadow);
    padding: 2rem;
}
.detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.25rem;
}
.detail-field {
    display: flex;
    flex-direction: column;
    font-size: 1.5rem;
}
.detail-label {
    color: #9ca3af;
    font-size: 1.2rem;
    margin-bottom: 0.25rem;
}
.info-box {
    border: 1px solid #d1d5db;
    border-radius: 0.75rem;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.image-area {
    border-radius: 0.75rem;
}
.product-image {
    width: 100%;
    border-radius: 0.75rem;
    object-fit: cover;
    box-shadow: var(--main-box-shadow);
}

aside{
    padding-top: 5rem;
}

</style>
